import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { DogListItem } from "@/components/DogListItem";
import { makeDogStateKey } from "@/lib/dogIdentity";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function DogList({
  dogs,
  selectedDayKey,
  completedDogs,
  urgentDogs,
  daysList,
  onDogClick,
  onUrgentClick,
  onDelete,
  onEdit,
  onReorder,
  currentDate,
  getInitialDayData,
  requireConfirmation,
  onApplyCanceledDaysBatch,
  isReadOnly,
  reorderUnlocked
}) {
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [pendingReorder, setPendingReorder] = useState(null);

  React.useEffect(() => {
    if (isReadOnly && pendingConfirmation) {
      setPendingConfirmation(null);
    }
  }, [isReadOnly, pendingConfirmation]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const getDogSortableId = (dog) => dog.id || dog.name;

  const handleDragEnd = ({ active, over }) => {
    if (isReadOnly || !reorderUnlocked || !over || active.id === over.id) return;
    setPendingConfirmation(null);
    if (typeof onReorder === "function") {
      const oldIndex = dogs.findIndex(d => getDogSortableId(d) === active.id);
      const newIndex = dogs.findIndex(d => getDogSortableId(d) === over.id);
      const newOrder = arrayMove(dogs, oldIndex, newIndex).map((dog, i) => ({
        ...dog,
        originalOrder: i
      }));
      const movedDog = dogs.find(d => getDogSortableId(d) === active.id);
      setPendingReorder({ newOrder, dogName: movedDog?.name });
    }
  };

  const handleDogClickIntercepted = (dogName, dogId) => {
    if (isReadOnly) return;
    if (!requireConfirmation) {
      onDogClick(dogName, dogId);
      return;
    }

    if (pendingConfirmation) {
      setPendingConfirmation(null);
      return;
    }

    const dogKey = makeDogStateKey(selectedDayKey, { id: dogId, name: dogName });
    const isCurrentlyCompleted = !!(completedDogs?.[dogKey]);
    setPendingConfirmation({ dogName, dogId, type: 'marking', isCurrentlyCompleted });
  };

  const handleUrgentClickIntercepted = (dogName, dogId, dateKeyOverride) => {
    if (isReadOnly) return;
    const targetDateKey = dateKeyOverride !== undefined ? dateKeyOverride : selectedDayKey;
    
    if (!requireConfirmation) {
      onUrgentClick(dogName, dogId, targetDateKey);
      return;
    }

    if (pendingConfirmation) {
      setPendingConfirmation(null);
      return;
    }

    const dogKey = makeDogStateKey(targetDateKey, { id: dogId, name: dogName });
    const isCurrentlyUrgent = !!(urgentDogs?.[dogKey]);
    setPendingConfirmation({ dogName, dogId, type: 'urgent', isCurrentlyUrgent, dateKey: targetDateKey });
  };

  const handleConfirmAction = () => {
    if (isReadOnly) return;
    if (pendingConfirmation) {
      if (pendingConfirmation.type === 'urgent') {
        onUrgentClick(pendingConfirmation.dogName, pendingConfirmation.dogId);
      } else {
        onDogClick(pendingConfirmation.dogName, pendingConfirmation.dogId);
      }
      setPendingConfirmation(null);
    }
  };

  const handleCancelAction = () => {
    setPendingConfirmation(null);
  };

  const handleApplyReorder = (scope) => {
    if (pendingReorder && typeof onReorder === "function") {
      onReorder(pendingReorder.newOrder, scope);
    }
    setPendingReorder(null);
  };

  if (!dogs || dogs.length === 0) {
    return <p className="text-center text-muted-foreground mt-8">No hay clientes para este día.</p>;
  }

  return (
    <>
      <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={dogs.map(getDogSortableId)}
        strategy={verticalListSortingStrategy}
      >
        <div
          className="space-y-0"
          onClick={() => { if (pendingConfirmation) setPendingConfirmation(null); }}
        >
           {dogs.map((dog, index) => (
             <DogListItem
               key={`${selectedDayKey}-${getDogSortableId(dog)}`}
               dog={dog}
               index={index}
               selectedDayKey={selectedDayKey}
               completedDogs={completedDogs}
               urgentDogs={urgentDogs}
               daysList={daysList}
               onDogClick={handleDogClickIntercepted}
               onUrgentClick={handleUrgentClickIntercepted}
               onDelete={onDelete}
               onEdit={onEdit}
               currentDate={currentDate}
               getInitialDayData={getInitialDayData}
               pendingConfirmation={pendingConfirmation}
               onConfirmAction={handleConfirmAction}
               onCancelAction={handleCancelAction}
               requireConfirmation={requireConfirmation}
               onApplyCanceledDaysBatch={onApplyCanceledDaysBatch}
               isReadOnly={isReadOnly}
             />
           ))}
        </div>
      </SortableContext>
      </DndContext>

      <Dialog open={!!pendingReorder} onOpenChange={(open) => { if (!open) setPendingReorder(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>¿Cómo quieres aplicar el cambio de {pendingReorder?.dogName}?</DialogTitle>
            <DialogDescription>Elige si este nuevo orden será puntual o permanente.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => handleApplyReorder('day')}>Solo este día</Button>
            <Button onClick={() => handleApplyReorder('future')}>Desde este día en adelante</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
